import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XCircle, AlertTriangle, Loader2, ArrowLeft, Calendar } from 'lucide-react';

type State = 'default' | 'loading' | 'error' | 'invalid';

interface CancelResponse {
  success: boolean;
  status?: string;
  redirectUrl?: string;
  message?: string;
}

export default function CancelBookingPage() {
  const params = new URLSearchParams(window.location.search);
  const bookingId = params.get('bookingId');
  const token = params.get('token');

  const isInvalid = !bookingId || !token;

  const [state, setState] = useState<State>(isInvalid ? 'invalid' : 'default');
  const [errorMessage, setErrorMessage] = useState<string>('');

  const handleCancel = async () => {
    if (!bookingId || !token) {
      setErrorMessage('Ungültiger Stornierungslink');
      setState('error');
      return;
    }

    if (state === 'loading') return;
    setState('loading');

    try {
      const res = await fetch('https://n8n.cogniiq.co/webhook/cancel-booking', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bookingId, token }),
      });

      if (!res.ok) {
        setErrorMessage('Netzwerkfehler. Bitte erneut versuchen.');
        setState('error');
        return;
      }

      const data: CancelResponse = await res.json();

      if (data.success && data.redirectUrl) {
        window.location.href = data.redirectUrl;
        return;
      }

      if (!data.success) {
        setErrorMessage(data.message || 'Stornierung fehlgeschlagen');
        setState('error');
        return;
      }
    } catch {
      setErrorMessage('Netzwerkfehler. Bitte erneut versuchen.');
      setState('error');
    }
  };

  return (
    <div className="min-h-screen svh-canvas flex flex-col items-center justify-center px-4 py-16">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-[560px]"
      >
        {/* Brand header */}
        <div className="text-center mb-8">
          <img
            src="/photos/Image.jpeg"
            alt="SV Heinersreuth"
            className="h-14 w-14 rounded-full object-cover mx-auto mb-3 shadow-sm ring-1 ring-black/5"
          />
          <p className="text-[15px] font-bold tracking-tight" style={{ color: 'var(--svh-ink)' }}>
            SV Heinersreuth
          </p>
          <p className="svh-label mt-1">Padel &amp; Tennis</p>
        </div>

        <AnimatePresence mode="wait">
          {/* ─── INVALID ─── */}
          {state === 'invalid' && (
            <motion.div
              key="invalid"
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.97 }}
              transition={{ duration: 0.3 }}
              className="svh-card svh-card-lg svh-card-raised overflow-hidden"
            >
              <div className="h-1 w-full" style={{ background: 'var(--svh-crest)' }} />
              <div className="p-8 md:p-10">
                <div className="flex items-center justify-center mb-6">
                  <div className="w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center">
                    <AlertTriangle className="w-7 h-7 text-amber-600" strokeWidth={1.75} />
                  </div>
                </div>
                <h1 className="svh-h3 text-center mb-3">Ungültiger Stornierungslink</h1>
                <p className="svh-body text-center mb-8">
                  Dieser Link ist unvollständig oder nicht gültig. Bitte verwenden Sie den Link aus Ihrer Bestätigungs-E-Mail.
                </p>
                <a href="/" className="svh-btn svh-btn--secondary svh-focus w-full">
                  <ArrowLeft className="w-4 h-4" />
                  Zur Startseite
                </a>
              </div>
            </motion.div>
          )}

          {/* ─── DEFAULT ─── */}
          {state === 'default' && (
            <motion.div
              key="default"
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.97 }}
              transition={{ duration: 0.3 }}
              className="svh-card svh-card-lg svh-card-raised overflow-hidden"
            >
              <div className="h-1 w-full" style={{ background: 'var(--svh-crest)' }} />
              <div className="p-8 md:p-10">
                <div className="text-center mb-8">
                  <p className="svh-eyebrow mb-3">Stornierung</p>
                  <h1 className="svh-h2 mb-4">Schade, dass Sie nicht spielen können.</h1>
                  <p className="svh-body font-medium" style={{ color: 'var(--svh-ink)' }}>
                    Möchten Sie diese Buchung wirklich stornieren?
                  </p>
                </div>

                <div
                  className="rounded-[10px] px-5 py-4 mb-6"
                  style={{ background: 'var(--svh-surface-sub)', border: '1px solid var(--svh-border)' }}
                >
                  <div className="flex items-start gap-3">
                    <Calendar className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--svh-ink-3)' }} />
                    <div className="flex-1 min-w-0">
                      <p className="svh-label mb-1">Buchungsreferenz</p>
                      <p className="font-mono text-[13px] leading-relaxed break-all" style={{ color: 'var(--svh-ink-2)' }}>
                        {bookingId}
                      </p>
                    </div>
                  </div>
                </div>

                <p className="svh-meta text-center mb-6">
                  Für die Stornierung gelten die Stornierungsbedingungen Ihrer Buchung.
                </p>

                <hr className="svh-rule mb-6" />

                <div className="space-y-3">
                  <button
                    onClick={handleCancel}
                    className="svh-btn svh-btn--primary svh-focus w-full"
                  >
                    Buchung jetzt stornieren
                  </button>

                  <a href="/" className="svh-btn svh-btn--secondary svh-focus w-full">
                    <ArrowLeft className="w-4 h-4" />
                    Buchung behalten – zur Startseite
                  </a>
                </div>
              </div>
            </motion.div>
          )}

          {/* ─── LOADING ─── */}
          {state === 'loading' && (
            <motion.div
              key="loading"
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.97 }}
              transition={{ duration: 0.3 }}
              className="svh-card svh-card-lg svh-card-raised overflow-hidden"
            >
              <div className="h-1 w-full" style={{ background: 'var(--svh-crest)' }} />
              <div className="p-8 md:p-10">
                <div className="flex flex-col items-center justify-center py-6 gap-5 text-center">
                  <div
                    className="w-14 h-14 rounded-full flex items-center justify-center"
                    style={{ background: 'var(--svh-crest-wash)' }}
                  >
                    <Loader2 className="w-7 h-7 animate-spin" style={{ color: 'var(--svh-crest)' }} strokeWidth={2} />
                  </div>
                  <div>
                    <p className="svh-h3 mb-1.5">Ihre Stornierung wird bearbeitet …</p>
                    <p className="svh-meta">Einen Moment bitte. Bitte schließen Sie dieses Fenster nicht.</p>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* ─── ERROR ─── */}
          {state === 'error' && (
            <motion.div
              key="error"
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.97 }}
              transition={{ duration: 0.35 }}
              className="svh-card svh-card-lg svh-card-raised overflow-hidden"
            >
              <div className="h-1 w-full" style={{ background: 'var(--svh-crest)' }} />
              <div className="p-8 md:p-10">
                <div className="flex items-center justify-center mb-6">
                  <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center">
                    <XCircle className="w-7 h-7 text-red-600" strokeWidth={1.75} />
                  </div>
                </div>
                <h1 className="svh-h3 text-center mb-3">Die Stornierung konnte nicht durchgeführt werden</h1>
                <p className="svh-body text-center mb-8">
                  {errorMessage || 'Die Stornierung konnte nicht verarbeitet werden.'}
                </p>
                <div className="space-y-3">
                  <button onClick={() => setState('default')} className="svh-btn svh-btn--primary svh-focus w-full">
                    Erneut versuchen
                  </button>
                  <a href="/" className="svh-btn svh-btn--secondary svh-focus w-full">
                    <ArrowLeft className="w-4 h-4" />
                    Zur Startseite
                  </a>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <p className="svh-meta text-center mt-6">SV Heinersreuth e.V.</p>
      </motion.div>
    </div>
  );
}
